import { sum } from 'd3-array'
import * as t from 'io-ts'
import { parseHostname } from '../lib/parseHostname'
import { BallotState, __BallotState } from './ballot-state'
import { emptyMultiLocaleString, MultiLocaleString } from './multi-locale-string'
import { PublicationMatcher, _casePublication } from './publication'

export interface Canton {
  cantonId: CantonId

  // The feature id inside the map. 1..26
  featureId: number

  // Weight of the canton for the purpose of counting 'Ständemehr'
  weight: 1 | 0.5
}

export const cantons: Array<Canton> = [
  { cantonId: 'ZH' as CantonId, featureId: 1, weight: 1 },
  { cantonId: 'BE' as CantonId, featureId: 2, weight: 1 },
  { cantonId: 'LU' as CantonId, featureId: 3, weight: 1 },
  { cantonId: 'UR' as CantonId, featureId: 4, weight: 1 },
  { cantonId: 'SZ' as CantonId, featureId: 5, weight: 1 },
  { cantonId: 'OW' as CantonId, featureId: 6, weight: 0.5 },
  { cantonId: 'NW' as CantonId, featureId: 7, weight: 0.5 },
  { cantonId: 'GL' as CantonId, featureId: 8, weight: 1 },
  { cantonId: 'ZG' as CantonId, featureId: 9, weight: 1 },
  { cantonId: 'FR' as CantonId, featureId: 10, weight: 1 },
  { cantonId: 'SO' as CantonId, featureId: 11, weight: 1 },
  { cantonId: 'BS' as CantonId, featureId: 12, weight: 0.5 },
  { cantonId: 'BL' as CantonId, featureId: 13, weight: 0.5 },
  { cantonId: 'SH' as CantonId, featureId: 14, weight: 1 },
  { cantonId: 'AR' as CantonId, featureId: 15, weight: 0.5 },
  { cantonId: 'AI' as CantonId, featureId: 16, weight: 0.5 },
  { cantonId: 'SG' as CantonId, featureId: 17, weight: 1 },
  { cantonId: 'GR' as CantonId, featureId: 18, weight: 1 },
  { cantonId: 'AG' as CantonId, featureId: 19, weight: 1 },
  { cantonId: 'TG' as CantonId, featureId: 20, weight: 1 },
  { cantonId: 'TI' as CantonId, featureId: 21, weight: 1 },
  { cantonId: 'VD' as CantonId, featureId: 22, weight: 1 },
  { cantonId: 'VS' as CantonId, featureId: 23, weight: 1 },
  { cantonId: 'NE' as CantonId, featureId: 24, weight: 1 },
  { cantonId: 'GE' as CantonId, featureId: 25, weight: 1 },
  { cantonId: 'JU' as CantonId, featureId: 26, weight: 1 },
]

export const lookupCanton = (id: CantonId): Canton => {
  const cd = cantons.find((x) => x.cantonId === unCantonId(id))
  if (cd === undefined) {
    // TODO: Send the error to sentry
    console.warn(`lookupCanton: invalid CantonId ${id}`)
    return { cantonId: '<CantonId>' as CantonId, featureId: 0, weight: 1 }
  } else {
    return cd
  }
}

export const lookupCantonByFeatureId = (id: number): Canton => {
  const cd = cantons.find((x) => x.featureId === id)
  if (cd === undefined) {
    // TODO: Send the error to sentry
    console.warn(`lookupCantonByFeatureId: invalid FeatureId ${id}`)
    return { cantonId: '<CantonId>' as CantonId, featureId: 0, weight: 1 }
  } else {
    return cd
  }
}

const allCantonIds = new Set(cantons.map((x) => x.cantonId))

/**
 * The sum of all canton weights.
 */
export const totalCantonWeight = sum(cantons, (x) => x.weight)

/**
 * CantonId is a newtype around 'string'. We check whether this string is
 * part of the allowed cantons at runtime and warn for unknown cantons.
 */
export interface CantonIdBrand {
  readonly CantonId: unique symbol
}

export const isCantonId = (x: string): x is t.Branded<string, CantonIdBrand> => allCantonIds.has(x as CantonId)

export const CantonId = t.brand(t.string, isCantonId, 'CantonId')

export type CantonId = t.TypeOf<typeof CantonId>

/**
 * CantonState collects information about a canton.
 */
export interface CantonState {
  id: CantonId
  state: BallotState

  /**
   * What is this used for?
   */
  info: MultiLocaleString
}

/**
 * Return the CantonId value as a string
 */
export const unCantonId = (x: CantonId): string => x

/**
 * Create a new CantonId from a string in range [0..1]. If the input
 * is outside of that range, a warning is printed to the console.
 */
export const mkCantonId = (x: string): CantonId => {
  if (!allCantonIds.has(x as CantonId)) {
    console.warn(`mkCantonId: unknown CantonId "${x}"`)
  }

  return x as CantonId
}

export type MajorityOfCantons =
  | 'none' // not required for the ballot
  | 'hidden' // required but don't show it in the UI
  | 'visible' // required and show it

export interface MajorityOfCantonsMatcher<T> {
  none: () => T
  hidden: () => T
  visible: () => T
}

export const caseMajorityOfCantons =
  <T>(m: MajorityOfCantonsMatcher<T>) =>
  (majorityOfCantons: MajorityOfCantons): T =>
    m[majorityOfCantons]()

/*
 * Maps the publication name (a string) to the preferred canton that should be shown
 * at the top of the list.
 */

export const publicationCantonPreferenceList: PublicationMatcher<CantonId[]> = {
  '24heures': [
    mkCantonId('VD'),
    mkCantonId('GE'),
    mkCantonId('VS'),
    mkCantonId('FR'),
    mkCantonId('NE'),
    mkCantonId('JU'),
    mkCantonId('ZH'),
  ],
  bazonline: [mkCantonId('BS'), mkCantonId('BL')],
  bernerzeitung: [mkCantonId('BE')],
  derbund: [mkCantonId('BE')],
  landbote: [mkCantonId('ZH')],
  lematin: [mkCantonId('GE')],
  tagesanzeiger: [mkCantonId('ZH')],
  tdg: [
    mkCantonId('GE'),
    mkCantonId('VD'),
    mkCantonId('VS'),
    mkCantonId('FR'),
    mkCantonId('NE'),
    mkCantonId('JU'),
    mkCantonId('ZH'),
  ],
  zsz: [mkCantonId('ZH')],
  zuonline: [mkCantonId('ZH')],

  // For testing, when accessing the app through the firebaseapp.com or web.app domains.
  web: [mkCantonId('ZH'), mkCantonId('ZG'), mkCantonId('GR')],
  firebaseapp: [mkCantonId('UR'), mkCantonId('AI'), mkCantonId('TI')],
}

/*
 * Return a list of 'CantonId's, based on the publication's preference.
 * (preferred cantons first, in the exact same order as specified in
 * 'publicationCantonPreferenceList', followed by the other cantons in
 * default order).
 */
export function publicationPreferredCantonIds(): CantonId[] {
  return publicationPreferredCantonIds_(parseHostname().publicationName)
}

export function publicationPreferredCantonIds_(publication: string): CantonId[] {
  const preferredCantonIds = _casePublication(publicationCantonPreferenceList, publication)

  const cantonIds = cantons.map((x) => x.cantonId)

  if (preferredCantonIds) {
    return [...preferredCantonIds, ...cantonIds.filter((x) => !preferredCantonIds.includes(x))]
  } else {
    return cantonIds
  }
}

// ----------------------------------------------------------------------------
// Catalog

export const __CantonState = {
  pre: [
    { id: mkCantonId('ZH'), state: __BallotState.pre, info: emptyMultiLocaleString },
    { id: mkCantonId('AR'), state: __BallotState.pre, info: emptyMultiLocaleString },
    { id: mkCantonId('UR'), state: __BallotState.pre, info: emptyMultiLocaleString },
    { id: mkCantonId('VD'), state: __BallotState.pre, info: emptyMultiLocaleString },
    { id: mkCantonId('LU'), state: __BallotState.pre, info: emptyMultiLocaleString },
    { id: mkCantonId('OW'), state: __BallotState.pre, info: emptyMultiLocaleString },
  ],

  mixed: [
    { id: mkCantonId('ZH'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('AR'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('UR'), state: __BallotState.trend.nay, info: emptyMultiLocaleString },
    { id: mkCantonId('VD'), state: __BallotState.projection.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('LU'), state: __BallotState.finalResult.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('OW'), state: __BallotState.finalResult.nay, info: emptyMultiLocaleString },
  ],

  inProgress: [
    { id: mkCantonId('ZH'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('AR'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('UR'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('VD'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('LU'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
    { id: mkCantonId('OW'), state: __BallotState.inProgress, info: emptyMultiLocaleString },
  ],

  trend: [
    { id: mkCantonId('ZH'), state: __BallotState.trend.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('AR'), state: __BallotState.trend.nay, info: emptyMultiLocaleString },
    { id: mkCantonId('UR'), state: __BallotState.trend.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('VD'), state: __BallotState.trend.nay, info: emptyMultiLocaleString },
    { id: mkCantonId('LU'), state: __BallotState.trend.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('OW'), state: __BallotState.trend.nay, info: emptyMultiLocaleString },
  ],

  projection: [
    { id: mkCantonId('ZH'), state: __BallotState.projection.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('AR'), state: __BallotState.projection.nay, info: emptyMultiLocaleString },
    { id: mkCantonId('UR'), state: __BallotState.projection.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('VD'), state: __BallotState.projection.nay, info: emptyMultiLocaleString },
    { id: mkCantonId('LU'), state: __BallotState.projection.yea, info: emptyMultiLocaleString },
    { id: mkCantonId('OW'), state: __BallotState.projection.nay, info: emptyMultiLocaleString },
  ],

  finalResult: cantons.map(({ cantonId }, i) => ({
    id: cantonId,
    state: (
      {
        0: __BallotState.finalResult.yea,
        1: __BallotState.finalResult.nay,
        2: __BallotState.finalResult.yea,
      } as const
    )[(i % 3) as 0 | 1 | 2],
    info: emptyMultiLocaleString,
  })),
}
